<script setup>
import { computed } from 'vue';
import { getTranslation } from '@/assets/scripts/getTranslation';
import translations from '@/assets/translations/Tr_Deeplinks.json';
import { useRoute } from 'vue-router';

const route = useRoute();

const otpCode = route.query["otpcode"];
const type = route.query["type"];

const showOtpBox = computed(() => {
  return type != "activation-email" && !!otpCode
});
</script>

<template>
  <main class="page-layout school-bg">

    <!-- Logo -->
    <div class="logo mb-4 text-center">
      <img :src="require(`@/assets/img/Dekuple/MontessoriLogo_${$i18n.locale}.svg`)" alt="logo Montessori">
    </div>

    <!-- White box (+ animated kids) -->
    <div class="whitebox gap-4 d-flex flex-column align-items-center">

      <!-- OTP -->
      <div class="whitebox__otpcode" v-if="showOtpBox">
        <div class="whitebox__title text-center">
          {{ getTranslation('otp-title', translations, $i18n.locale) }}
        </div>
        <div class="whitebox__subtitle w-100 text-center">
          <div class="whitebox__otp">
            {{ otpCode }}
          </div>
          {{ getTranslation('otp-subtitle', translations, $i18n.locale) }}
        </div>
      </div>

      <!-- Default box (no otp) -->
      <div class="whitebox__title text-center" v-else>
        <div class="whitebox__title">
          {{ getTranslation('title-label', translations, $i18n.locale) }}
        </div>
        <div class="whitebox__subtitle w-100 text-center">
          {{ getTranslation('subtitle-label', translations, $i18n.locale) }}
          <br>
          {{ getTranslation('subtitle-label-2', translations, $i18n.locale) }}
        </div>
      </div>

      <!-- Store Buttons -->
      <div class="whitebox__activation d-md-grid d-flex flex-column gap-lg-2 gap-1">
        <a href="https://appsto.re/ca/lOY2db.i?l=en" class="whitebox__activation-apple whitebox__activation-link"
          target="_blank">
          <img src="@/assets/img/AppStore-button.svg" alt="" class="whitebox__activation-img">
        </a>
        <a href="https://play.google.com/store/apps/details?id=com.edokicademy.montessoriacademy"
          class="whitebox__activation-google whitebox__activation-link" target="_blank">
          <img src="@/assets/img/GooglePlay-button.svg" alt="" class="whitebox__activation-img">
        </a>
        <a href="https://www.amazon.com/Edoki-Academy-Montessori-Preschool/dp/B0747ZJ2C2"
          class="whitebox__activation-amazon whitebox__activation-link" target="_blank">
          <img src="@/assets/img/Amazon-button.svg" alt="" class="whitebox__activation-img">
        </a>
      </div>

      <div class="whitebox__subtitle" v-if="!otpCode">
        {{ getTranslation('next-label', translations, $i18n.locale) }}
      </div>

      <div class="whitebox__contact">
        <a :href="`https://edokiacademy.freshdesk.com/${$i18n.locale}/support/solutions/folders/44001232982`"
          target="_blank">
          {{ getTranslation('contact-label', translations, $i18n.locale) }}
        </a>
      </div>

      <div class="whitebox__characters">
        <img src="@/assets/img/Dekuple/GraduateGirl_AVosKids.gif" alt=""
          class="whitebox__character whitebox__character--girl">
        <img src="@/assets/img/Dekuple/GraduateBoy_AVosKids.gif" alt=""
          class="whitebox__character whitebox__character--boy">
      </div>
    </div>

  </main>
</template>

<style lang="scss">
.page-layout {
  display: grid;
  grid-template-rows: auto 100fr;
  justify-content: center;
  align-items: center;
  justify-items: center;
  padding: 2rem 0;
}

.school-bg {
  background-image: url(@/assets/img/Dekuple/Background02.svg);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  background-color: #5fe1ff;
}

.whitebox {
  position: relative;
  width: 50vw;
  max-width: 900px;
  padding: 2rem 0;
  background-color: #fff;
  border-radius: 2rem;

  &__title {
    font-family: 'Sofia-Pro-Soft', sans-serif;
    font-size: 2rem;
    font-weight: 700;
    color: #00c2ff;
  }

  &__subtitle {
    font-weight: 600;
    font-size: 1.2rem;
    text-align: center;
    color: #183951;
    width: 70%;
  }

  &__otp {
    font-size: 4rem;
    font-weight: 700;
    letter-spacing: 1rem;
    margin-bottom: 1rem;
  }

  &__next {
    font-weight: 600;
    font-size: 1.2rem;
    width: 60%;
    text-align: center;
  }

  &__contact {
    text-align: center;
    font-weight: 500;
    font-size: 0.8rem;

    a {
      text-decoration: none;
      cursor: pointer;
      color: #183951;
    }
  }

  &__activation {
    grid-template-columns: auto auto;
    justify-items: center;

    &-apple {
      grid-column: 1/3;
    }

    &-google {
      grid-row: 2;
    }

    &-amazon {
      grid-row: 2;
    }
  }

  &__character {
    width: 20rem;
    position: absolute;
    top: 6rem;

    &--girl {
      left: -15rem;
    }

    &--boy {
      right: -15rem;
    }
  }
}

@media (max-width: 992px) {

  .logo {
    img {
      width: 80%;
    }
  }

  .whitebox {
    &__title {
      font-size: 1.7rem;
    }

    &__subtitle {
      font-size: 1rem;
    }

    &__next {
      font-size: 0.8rem;
    }

    &__activation {
      &-link {
        width: 10rem;
      }

      &-img {
        width: inherit;
      }
    }

    &__character {
      width: 16rem;
      top: 7rem;

      &--boy {
        right: -12rem;
      }

      &--girl {
        left: -12rem;
      }
    }
  }
}

@media (max-width: 768px) {

  .page-layout {
    background-size: cover;
    padding-bottom: 8rem;
  }

  .logo {
    margin-bottom: 1rem;

    img {
      width: 60%;
    }
  }

  .whitebox {
    gap: 1rem;
    width: 90vw;
    padding: 1.5rem 0 10rem;

    &__otp {
      font-size: 2.5rem;
    }

    &__character {
      position: absolute;
      top: 29rem;
      width: 15rem;

      &--girl {
        left: -1rem;
      }

      &--boy {
        right: -1rem;
      }
    }
  }
}

@media (max-width: 459px) {

  .page-layout {
    padding-bottom: 9rem;
  }

  .whitebox {
    padding-bottom: 6rem;

    &__character {
      width: 12rem;
      top: auto;
      bottom: -8rem;
    }

    &__title {
      font-size: 1.5rem;
    }

    &__subtitle {
      font-size: 1rem;
    }

    &__next {
      font-size: 1rem;
    }

    &__contact {
      width: 50%;
      font-size: 1rem;
    }
  }
}

@media (min-width: 1441px) {

  .whitebox {
    &__title {
      font-size: 2.5rem;
    }

    &__subtitle {
      font-size: 1.5rem;
    }

    &__next {
      font-size: 1.2rem;
    }

    &__contact {
      font-size: 1rem;
    }

    &__character {
      width: 25rem;
      top: 3rem;

      &--girl {
        left: -19rem;
        top: 4rem;
      }

      &--boy {
        right: -19rem;
      }
    }
  }
}
</style>